import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { setActionLoader } from "../../../../../../../../../features/aiRecomendationTabSlice";
import { useDispatch } from "react-redux";

interface SecondSaveChangesAiProps {
  open: boolean;
  rowData: any;
  onClose: () => void;
  onConfirm?: (groupedData: any) => Promise<void>;
}

const SecondSaveChangesAi = ({
  open,
  onClose,
  onConfirm,
}: SecondSaveChangesAiProps) => {
  const {
    selectedPositiveKeywordDefinitions,
    selectedNegativeKeywordDefinitions,
    actionLoader,
  } = useSelector((state: any) => state.aiRecomandationTab);
  const dispatch = useDispatch();

  // Process definitions and group by definition type
  const processedData = useMemo(() => {
    // Initialize arrays for each definition type
    const exclusiveRows: any[] = [];
    const allowRows: any[] = [];
    const negativeListRows: any[] = [];
    const removeNegativeRows: any[] = [];

    const adgroupsSet = new Set();
    let index = 1;

    // Process Positive Keyword Definitions
    Object.entries(selectedPositiveKeywordDefinitions || {}).forEach(
      ([_, item]: [string, any]) => {
        if (item.adgroupName) {
          adgroupsSet.add(item.adgroupName);
        }

        const rowData = {
          id: index++,
          searchTerm: item.search_term || "",
          adgroupName: item.adgroupName || "",
          matchType: item.search_term_match_type || "",
          keyword_shared_uid: item.keyword_shared_uid,
          keyword_id: item.keyword_id,
          adgroup_shared_uid: item.adgroup_shared_uid,
          search_term_shared_uid: item.search_term_shared_uid,
        };

        switch (item.definition) {
          case "exclusive":
            exclusiveRows.push(rowData);
            break;
          case "allow":
            allowRows.push(rowData);
            break;
          case "negative":
            negativeListRows.push(rowData);
            break;
        }
      }
    );

    // Process Negative Keyword Definitions
    Object.entries(selectedNegativeKeywordDefinitions || {}).forEach(
      ([_, item]: [string, any]) => {
        if (item.adgroupName) {
          adgroupsSet.add(item.adgroupName);
        }

        const rowData = {
          id: index++,
          searchTerm: item.search_term || "",
          adgroupName: item.adgroupName || "",
          matchType: item.search_term_match_type || "",
          keyword_shared_uid: item.keyword_shared_uid,
          keyword_id: item.keyword_id,
          adgroup_shared_uid: item.adgroup_shared_uid,
          search_term_shared_uid: item.search_term_shared_uid,
        };

        if (item.definition === "mark-as-removed") {
          removeNegativeRows.push(rowData);
        }
      }
    );

    return {
      exclusiveRows,
      allowRows,
      negativeListRows,
      removeNegativeRows,
      totalAdgroups: adgroupsSet.size,
    };
  }, [selectedPositiveKeywordDefinitions, selectedNegativeKeywordDefinitions]);

  // Define columns
  const columns: GridColDef[] = [
    {
      field: "searchTerm",
      headerName: "Search Term",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "adgroupName",
      headerName: "Adgroup Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "matchType",
      headerName: "Match Type",
      flex: 0.6,
      minWidth: 130,
    },
  ];

  const handleConfirm = async () => {
    dispatch(setActionLoader(true));

    try {
      if (onConfirm) {
        // Pass the processed data to parent for API calls
        await onConfirm({
          exclusive: processedData.exclusiveRows,
          allow: processedData.allowRows,
          negativeList: processedData.negativeListRows,
          removeNegative: processedData.removeNegativeRows,
          positiveDefinitions: selectedPositiveKeywordDefinitions,
          negativeDefinitions: selectedNegativeKeywordDefinitions,
        });
      }
    } catch (error) {
      console.error("Error confirming changes:", error);
    } finally {
      dispatch(setActionLoader(false));
    }
  };

  const totalChanges =
    processedData.exclusiveRows.length +
    processedData.allowRows.length +
    processedData.negativeListRows.length +
    processedData.removeNegativeRows.length;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent close if loading and the reason is backdrop click or escape key
        if (
          actionLoader &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        ) {
          return;
        }
        onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Total Number of Adgroups Affected: {processedData.totalAdgroups}
      </DialogTitle>
      <DialogContent>
        {totalChanges === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No changes to save
            </Typography>
          </Box>
        ) : (
          <>
            {/* Set as Exclusive Section */}
            {processedData.exclusiveRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Set as Exclusive ({processedData.exclusiveRows.length})
                </Typography>
                <Box sx={{ width: "100%", display: "grid", mt: 1 }}>
                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                        {
                          outline: "none",
                        },
                      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                        { outline: "none" },
                      "& .MuiDataGrid-cellEmpty": {
                        width: "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "& .MuiDataGrid-row.Mui-selected": {
                        backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                        "&:hover": {
                          backgroundColor:
                            "rgba(156, 39, 176, 0.16) !important",
                        },
                      },
                      "& .MuiDataGrid-cell.Mui-selected": {
                        backgroundColor: "inherit !important",
                      },
                      "& .MuiCheckbox-root": {
                        color: "#9c27b0 !important",
                      },
                      "& .MuiCheckbox-root.Mui-checked": {
                        color: "#9c27b0",
                      },
                      border: "none",
                    }}
                    density="compact"
                    hideFooter
                    pagination={false}
                    rows={processedData.exclusiveRows}
                    columns={columns}
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnReorder
                    sortingOrder={["asc", "desc"]}
                    isCellEditable={() => false}
                    disableRowSelectionOnClick
                  />
                </Box>
              </Box>
            )}

            {/* Set as Allow Section */}
            {processedData.allowRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Set as Allow ({processedData.allowRows.length})
                </Typography>
                <Box sx={{ width: "100%", display: "grid", mt: 1 }}>
                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                        {
                          outline: "none",
                        },
                      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                        { outline: "none" },
                      "& .MuiDataGrid-cellEmpty": {
                        width: "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "& .MuiDataGrid-row.Mui-selected": {
                        backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                        "&:hover": {
                          backgroundColor:
                            "rgba(156, 39, 176, 0.16) !important",
                        },
                      },
                      "& .MuiDataGrid-cell.Mui-selected": {
                        backgroundColor: "inherit !important",
                      },
                      "& .MuiCheckbox-root": {
                        color: "#9c27b0 !important",
                      },
                      "& .MuiCheckbox-root.Mui-checked": {
                        color: "#9c27b0",
                      },
                      border: "none",
                    }}
                    density="compact"
                    hideFooter
                    pagination={false}
                    rows={processedData.allowRows}
                    columns={columns}
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnReorder
                    sortingOrder={["asc", "desc"]}
                    isCellEditable={() => false}
                    disableRowSelectionOnClick
                  />
                </Box>
              </Box>
            )}

            {/* Negative List Section */}
            {processedData.negativeListRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Negative List ({processedData.negativeListRows.length})
                </Typography>
                <Box sx={{ width: "100%", display: "grid", mt: 1 }}>
                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                        {
                          outline: "none",
                        },
                      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                        { outline: "none" },
                      "& .MuiDataGrid-cellEmpty": {
                        width: "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "& .MuiDataGrid-row.Mui-selected": {
                        backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                        "&:hover": {
                          backgroundColor:
                            "rgba(156, 39, 176, 0.16) !important",
                        },
                      },
                      "& .MuiDataGrid-cell.Mui-selected": {
                        backgroundColor: "inherit !important",
                      },
                      "& .MuiCheckbox-root": {
                        color: "#9c27b0 !important",
                      },
                      "& .MuiCheckbox-root.Mui-checked": {
                        color: "#9c27b0",
                      },
                      border: "none",
                    }}
                    density="compact"
                    hideFooter
                    pagination={false}
                    rows={processedData.negativeListRows}
                    columns={columns}
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnReorder
                    sortingOrder={["asc", "desc"]}
                    isCellEditable={() => false}
                    disableRowSelectionOnClick
                  />
                </Box>
              </Box>
            )}

            {/* Remove Negative Section */}
            {processedData.removeNegativeRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Remove Negative ({processedData.removeNegativeRows.length})
                </Typography>
                <Box sx={{ width: "100%", display: "grid", mt: 1 }}>
                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                        {
                          outline: "none",
                        },
                      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                        { outline: "none" },
                      "& .MuiDataGrid-cellEmpty": {
                        width: "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "& .MuiDataGrid-row.Mui-selected": {
                        backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                        "&:hover": {
                          backgroundColor:
                            "rgba(156, 39, 176, 0.16) !important",
                        },
                      },
                      "& .MuiDataGrid-cell.Mui-selected": {
                        backgroundColor: "inherit !important",
                      },
                      "& .MuiCheckbox-root": {
                        color: "#9c27b0 !important",
                      },
                      "& .MuiCheckbox-root.Mui-checked": {
                        color: "#9c27b0",
                      },
                      border: "none",
                    }}
                    density="compact"
                    hideFooter
                    pagination={false}
                    rows={processedData.removeNegativeRows}
                    columns={columns}
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnReorder
                    sortingOrder={["asc", "desc"]}
                    isCellEditable={() => false}
                    disableRowSelectionOnClick
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: "16px 20px 16px" }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={actionLoader}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          color="secondary"
          disabled={actionLoader || totalChanges === 0}
          startIcon={
            actionLoader ? <CircularProgress size={20} color="inherit" /> : null
          }
          data-tracker="619"
        >
          {actionLoader ? "Processing..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SecondSaveChangesAi;
